import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Card, Button, CardContent, CardHeader } from 'semantic-ui-react';
import i18next from 'i18next';
import { toastr } from 'react-redux-toastr';
import AssetPicker from './AssetPicker';
import LayoutToggler from './LayoutToggler';
import BrandColorPicker from './BrandColorPicker';
import CssEditor from './CssEditor';
import { validateFileSize } from '../../../helpers/assets';
import { fetchFullBrandData, uploadBrandAsset, removeBrandAsset, confirmDelete, updateTheme } from '../../../actions';

import './BrandBuilder.scss';

const ASSET_UPLOAD_PLACEHOLDER = {};

class BrandBuilder extends Component {
    state = { layout: this.props.brand.platform === 'website' ? 'landscape' : 'portrait' };

    componentDidMount() {
        this.props.fetchFullBrandData(this.props.brand.key, this.props.brand.platform, this.props.handleBrandLoaded);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.brand.key !== this.props.brand.key) {
            this.props.fetchFullBrandData(this.props.brand.key, this.props.brand.platform, this.props.handleBrandLoaded);
        }
    }

    handleAssetUpload = (keyPath, file, keyFormat) => {
        try {
            validateFileSize(file, keyPath);

            const s3AssetKey = keyFormat.replace('{index}', new Date().getTime()).replace('{layout}', this.state.layout);

            this.props.uploadBrandAsset(this.props.brand.key, s3AssetKey, file, keyPath);
        } catch (err) {
            toastr.error(err);
        }
    }

    handleAssetRemove = (keyPath, key) => {
        this.props.confirmDelete(key, () => this.props.removeBrandAsset(this.props.brand.key, key, keyPath));
    }

    renderAsset = (assetKey, layout = '', hasMany = false) => {
        const { assets, keyFormat, size } = this.props.brand.assets[assetKey];

        return assets
            .filter(({ key }) => !keyFormat.includes('{layout}') || !layout || key.includes(layout))
            .concat(ASSET_UPLOAD_PLACEHOLDER)
            .slice(0, hasMany ? undefined : 1)
            .map(({ key, url }) => (
                <AssetPicker
                    key={key || 'add'}
                    className={`brand-asset ${layout} ${size}`}

                    source={url}
                    video={assetKey === 'videoBackground'}
                    onUpload={file => this.handleAssetUpload(assetKey, file, keyFormat)}
                    onDelete={() => this.handleAssetRemove(assetKey, key)}
                />
            ));
    }

    renderBackgroundsSection(platform) {
        const { layout } = this.state;

        return (
            <Card style={{ flex: 1, minWidth: 'fit-content', margin: 10, marginRight: 0, marginLeft: 10 }}>
                <CardHeader className="brand-card-header">
                    {`${i18next.t('brands.backgroundsManagement')} ${i18next.t(`brands.resolutions.${layout}`)}`}
                </CardHeader>
                <CardContent style={{ display: 'flex', overflowX: 'auto' }}>
                    <div className="brand-builder-cell">
                        {this.renderAsset('mainBackground', layout)}
                        <div className="brand-group-label">{i18next.t('brands.mainBackground')}</div>
                    </div>
                    {platform === 'kiosk' && (
                        <Fragment>
                            <div className="brand-builder-cell">
                                {this.renderAsset('secondaryBackground', layout)}
                                <div className="brand-group-label">{i18next.t('brands.secondaryBackground')}</div>
                            </div>
                            <div className="brand-builder-cell">
                                {this.renderAsset('videoBackground', layout)}
                                <div className="brand-group-label">{i18next.t('brands.videoBackground')}</div>
                            </div>
                        </Fragment>
                    )}
                </CardContent>
            </Card>
        );
    }

    renderMainPromotionsSection(platform) {
        const { layout } = this.state;

        return platform === 'kiosk' ? (
            <Card style={{ flex: 1, margin: 10, marginRight: 0, marginLeft: 10 }}>
                <CardHeader className="brand-card-header">
                    {`${i18next.t('brands.mainPromotions')} ${i18next.t('brands.resolutions.' + layout)}`}
                </CardHeader>
                <CardContent style={{ overflowX: 'auto' }}>
                    <div className="brand-builder-line">
                        <div className="brand-builder-cell" style={{ display: 'inline-block' }}>
                            <div className="brand-assets-carousel">
                                {this.renderAsset('mainSlideshow', layout, true)}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        ) : null;
    }

    render() {
        const { brand } = this.props;
        const { layout } = this.state;

        if (!brand.assets) return null;

        return (
            <div className="brand-builder">
                <LayoutToggler layout={layout} onLayoutChange={layout => this.setState({ layout })} />
                <div className="brand-builder-line row">
                    <div style={{ flex: 1, padding: 1 }}>
                        <Card fluid style={{ height: '100%', minWidth: 200 }}>
                            <Card.Content>
                                <Button
                                    floated='right'
                                    icon="file text"
                                    basic
                                    circular
                                    onClick={() => this.setState({ showCssModal: true })}
                                />
                                <CssEditor
                                    open={this.state.showCssModal}
                                    cssRawData={brand.theme.cssRawData}
                                    onSave={cssRawData => this.props.updateTheme(brand.key, { ...brand.theme, cssRawData }, brand.platform)}
                                    onClose={() => this.setState({ showCssModal: false })}
                                />
                                <Card.Header>{brand.key.toUpperCase()}</Card.Header>
                                <Card.Meta>{brand.key}</Card.Meta>
                            </Card.Content>
                            <Card.Content extra style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <BrandColorPicker
                                        color={brand.theme.colors.primary}
                                        text={i18next.t('brands.primaryColor')}
                                        onColorChange={color => {
                                            const { theme } = brand;
                                            theme.colors.primary = color;
                                            this.props.updateTheme(brand.key, theme, brand.platform);
                                        }}
                                    />
                                    <BrandColorPicker
                                        color={brand.theme.colors.secondary}
                                        text={i18next.t('brands.secondaryColor')}
                                        onColorChange={color => {
                                            const { theme } = brand;
                                            theme.colors.secondary = color;
                                            this.props.updateTheme(brand.key, theme, brand.platform);
                                        }}
                                    />
                                </div>
                            </Card.Content>
                        </Card>
                    </div>
                    <div className="brand-builder-cell">
                        {this.renderAsset('logo')}
                        <div className="brand-group-label">{i18next.t('logo')}</div>
                    </div>
                    <div className="brand-builder-cell">
                        {this.renderAsset('dishPlaceholder')}
                        <div className="brand-group-label">{i18next.t('brands.dishPlaceholder')}</div>
                    </div>
                </div>
                <div className="brand-builder-line row" style={{ overflow: 'unset', flexWrap: 'wrap' }}>
                    {this.renderBackgroundsSection(brand.platform)}
                    {this.renderMainPromotionsSection(brand.platform)}
                </div>
                {brand.platform === 'kiosk' && layout === 'portrait' &&
                    <Card fluid>
                        <CardHeader className="brand-card-header">
                            {`${i18next.t('brands.menuBanners')} ${i18next.t('brands.resolutions.banner')}`}
                        </CardHeader>
                        <CardContent style={{ overflowX: 'auto' }}>
                            <div className="brand-builder-line">
                                <div className="brand-builder-cell">
                                    <div className="brand-assets-carousel">
                                        {this.renderAsset('menuBanners', 'banner', true)}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                }
            </div >
        );
    }
}

BrandBuilder.propTypes = {
    fetchFullBrandData: PropTypes.func,
    uploadBrandAsset: PropTypes.func,
    handleBrandLoaded: PropTypes.func,
    removeBrandAsset: PropTypes.func,
    confirmDelete: PropTypes.func,
    updateTheme: PropTypes.func,
    brand: PropTypes.object,
    isRtl: PropTypes.bool
};

const mapStateToProps = (state, ownProps) => {
    const brandKey = ownProps.match.params.brandKey || ownProps.brandKey;

    const brandKeyParts = brandKey.split('-');
    const brand = state.businessApp.brands.find(b => b.key === brandKey) || { key: brandKey, platform: brandKeyParts.length > 1 ? brandKeyParts[brandKeyParts.length - 1] : undefined };

    return { brand };
};

export default connect(mapStateToProps, { fetchFullBrandData, uploadBrandAsset, removeBrandAsset, updateTheme, confirmDelete })(BrandBuilder);